import { Component, Input } from '@angular/core';
import { AbregelungKopplungsTyp } from '../../../../../domain/model/abregelung-kopplungs-typ.enum';
import { AbstractControlOptions, UntypedFormGroup, Validators } from '@angular/forms';
import {
    AbregelungsinformationenIec101104Adressen,
} from '../../../../../domain/model/abregelungsinformationen-iec101104-adressen.model';
import { RowDescriptor } from '../../row-descriptor';
import { ControlConfig, FormGroupConfig } from '../../../form-config.model';
import { ErzeugungsanlageConfig } from '../../../erzeugungsanlage.config';
import { MatValidators } from '../../../../../common/material/components/mat-form-field-required.directive';
import { Iec100104Validator } from '../../../validator/iec100104.validator';
import { Feld } from '../../../../../domain/model/feld.enum';

interface FeldMapping {
    kopplungsTyp: Feld
    hohesByte: Feld
    mittleresByte: Feld
    niedrigesByte: Feld
    typkennung: Feld
}

@Component({
    selector: 'redispatch-abregelinformationen-iec101104-adresse',
    templateUrl: './abregelinformationen-iec101104-adresse.component.html',
    styleUrls: [
        './abregelinformationen-iec101104-adresse.component.scss',
    ],
    standalone: false,
})
export class AbregelinformationenIec101104AdresseComponent {

    public static readonly BYTES_MIN = 0;
    public static readonly BYTES_MAX = 255;
    public static readonly GANZZALIG_PATTERN = new RegExp('^\\d*$');
    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly COLS = ErzeugungsanlageConfig.ABREGELINFOS_ADDR_IEC_FX_FLEX_COL;
    @Input({ required: true }) form: UntypedFormGroup;
    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;
    private readonly feldEntityMapping: {
        [params: string]: FeldMapping
    } = {
            SOLLWERT: {
                kopplungsTyp: Feld.FIELD_IEC_SOLLWERT_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_SOLLWERT_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_SOLLWERT_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_SOLLWERT_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_SOLLWERT_TYPKENNUNG,
            },
            TYP_100: {
                kopplungsTyp: Feld.FIELD_IEC_100_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_100_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_100_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_100_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_100_TYPKENNUNG,
            },
            TYP_60: {
                kopplungsTyp: Feld.FIELD_IEC_60_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_60_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_60_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_60_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_60_TYPKENNUNG,
            },
            TYP_30: {
                kopplungsTyp: Feld.FIELD_IEC_30_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_30_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_30_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_30_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_30_TYPKENNUNG,
            },
            TYP_0: {
                kopplungsTyp: Feld.FIELD_IEC_0_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_0_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_0_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_0_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_0_TYPKENNUNG,
            },
            WIRKLEISTUNG_P: {
                kopplungsTyp: Feld.FIELD_IEC_WIRKLEISTUNG_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_WIRKLEISTUNG_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_WIRKLEISTUNG_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_WIRKLEISTUNG_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_WIRKLEISTUNG_TYPKENNUNG,
            },
            BLINDLEISTUNG_Q: {
                kopplungsTyp: Feld.FIELD_IEC_BLINDLEISTUNG_KOPPLUNGSTYP,
                hohesByte: Feld.FIELD_IEC_BLINDLEISTUNG_HOHES_BYTE,
                mittleresByte: Feld.FIELD_IEC_BLINDLEISTUNG_MITTLERES_BYTE,
                niedrigesByte: Feld.FIELD_IEC_BLINDLEISTUNG_NIEDRIGES_BYTE,
                typkennung: Feld.FIELD_IEC_BLINDLEISTUNG_TYPKENNUNG,
            },
        };

    constructor() {
    }

    public get f(): ControlConfig<AbregelungsinformationenIec101104Adressen> {
        return this.form.controls as ControlConfig<AbregelungsinformationenIec101104Adressen>;
    }

    public get kopplungsTyp(): AbregelungKopplungsTyp {
        return this.f.kopplungsTyp.value;
    }

    public get typkennungValidValues(): string {
        return Iec100104Validator.getAllowedTypkennungValues(this.kopplungsTyp).toString();
    }

    get rowDesc(): RowDescriptor {
        return RowDescriptor.getNameForType(this.kopplungsTyp);
    }

    get placeholder(): string {
        if (!this.f.hohesByte.value && !this.f.mittleresByte.value &&
            !this.f.niedrigesByte.value && !this.f.typkennung.value) {
            if (!(this.form as any).customPassthroughRequired) {
                return 'Wert optional';
            }
        }
        return 'Wert eintragen';
    }

    get warningTooltip(): string {
        return this.form.hasError('fillAllValues') ? 'Bitte füllen Sie alle der Felder aus' : '';
    }

    get feldMapping(): FeldMapping | null {
        return this.feldEntityMapping[this.kopplungsTyp];
    }

    static formGroupConfig(
        initType: AbregelungKopplungsTyp,
        initialDisabled: boolean,
        required: boolean,
    ): FormGroupConfig<AbregelungsinformationenIec101104Adressen> {
        const validatorBytes = [
            Validators.min(AbregelinformationenIec101104AdresseComponent.BYTES_MIN),
            Validators.max(AbregelinformationenIec101104AdresseComponent.BYTES_MAX),
            Validators.pattern(AbregelinformationenIec101104AdresseComponent.GANZZALIG_PATTERN),
        ];
        const validatorTypekennung = [
            Validators.pattern(AbregelinformationenIec101104AdresseComponent.GANZZALIG_PATTERN),
            Iec100104Validator.validateTypkennung(initType),
        ];

        if (required) {
            validatorBytes.push(MatValidators.required);
            validatorTypekennung.push(MatValidators.required);
        }

        return {
            kopplungsTyp: [
                { value: initType, disabled: initialDisabled },
                [
                    MatValidators.required,
                ],
            ],
            hohesByte: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorBytes,
                ],
            ],
            mittleresByte: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorBytes,
                ],
            ],
            niedrigesByte: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorBytes,
                ],
            ],
            typkennung: [
                { value: null, disabled: initialDisabled },
                [
                    ...validatorTypekennung,
                ],
            ],
        } as FormGroupConfig<AbregelungsinformationenIec101104Adressen>;
    }

    static formOptions(required: boolean): AbstractControlOptions | null {
        return {
            validators: Iec100104Validator.allFieldsPresentValidator(required),
        };
    }

}
